/**
 * Theme provider and hook.
 * Manages the active color theme (light/dark) and persists the
 * user's choice in localStorage. Applies the theme by setting a
 * `data-theme` attribute on the document root element.
 *
 * @module dataProviders/ThemeProvider
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

const STORAGE_KEY   = 'sp-theme';
const DEFAULT_THEME = 'light';

/** @type {React.Context} */
const ThemeContext = createContext(null);

/**
 * Provides theme state to child components.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - Child components.
 * @returns {React.ReactElement}
 */
export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_THEME;
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  /**
   * Update the active theme.
   *
   * @param {string} newTheme - Theme name ('light' or 'dark').
   */
  const setTheme = (newTheme) => {
    setThemeState(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook for accessing the theme context.
 *
 * @returns {{ theme: string, setTheme: Function }}
 */
export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
